import { useMemo, useCallback } from 'react'
import { useLocalStorage } from 'react-use'
import isEmpty from 'lodash/isEmpty'
import { useUser, useUserTimeZone } from '@ztechapps/common.hook'
import { TaskStatusEnum } from '@ztechapps/common.contract'
import type { DateRange } from '@ztechapps/common.utils'
import { DateNameEnum, DateNameValue, getDynamicDateFilter, toDate } from '@ztechapps/common.utils'

const ALL_TASK_STATUSES = Object.values(TaskStatusEnum)

export const UNASSIGNED_FILTER_ID = '__unassigned__'

/**
 * Builds MongoDB conditions for assignee filtering.
 * Handles the special "__unassigned__" sentinel to match tasks with no members.
 */
const buildAssigneeCondition = (assigneeIds: string[]): Record<string, any> | null => {
  if (isEmpty(assigneeIds)) return null

  const hasUnassigned = assigneeIds.includes(UNASSIGNED_FILTER_ID)
  const realIds = assigneeIds.filter((id) => id !== UNASSIGNED_FILTER_ID)

  if (hasUnassigned && !isEmpty(realIds)) {
    return { $or: [{ members: { $size: 0 } }, { members: { $exists: false } }, { members: { $in: realIds } }] }
  } else if (hasUnassigned) {
    return { $or: [{ members: { $size: 0 } }, { members: { $exists: false } }] }
  }
  return { members: { $in: realIds } }
}

export type TaskFilterDateRange = {
  presetName?: DateNameEnum | null
  customStart?: string
  customEnd?: string
}

export type FilterOption = {
  _id: string
  label: string
}

export type TaskFilterState = {
  statuses: TaskStatusEnum[]
  projectIds: string[]
  workOrderIds: string[]
  assigneeIds: string[]
  dateRange: TaskFilterDateRange
  selectedProjectItems: FilterOption[]
  selectedWorkOrderItems: FilterOption[]
  selectedAssigneeItems: FilterOption[]
}

export const DEFAULT_TASK_FILTER_STATE: TaskFilterState = {
  statuses: [TaskStatusEnum.Draft, TaskStatusEnum.InProgress],
  projectIds: [],
  workOrderIds: [],
  assigneeIds: [],
  dateRange: {},
  selectedProjectItems: [],
  selectedWorkOrderItems: [],
  selectedAssigneeItems: [],
}

/**
 * Returns the runtime default filter state with the current user's ID.
 * Default = Draft + In Progress tasks assigned to me.
 */
export const getDefaultFilterState = (userId?: string): TaskFilterState => ({
  ...DEFAULT_TASK_FILTER_STATE,
  assigneeIds: userId ? [userId] : [],
})

export enum TaskFilterPresetEnum {
  MyTasks = 'myTasks',
  DueThisWeek = 'dueThisWeek',
  Overdue = 'overdue',
}

export type UseTaskFilterStateResult = {
  filterState: TaskFilterState
  mongoFilter: Record<string, any>
  nonStatusFilter: Record<string, any>
  setFilter: (partial: Partial<TaskFilterState>) => void
  clearFilter: (key: keyof TaskFilterState) => void
  clearAll: () => void
  applyPreset: (preset: TaskFilterPresetEnum) => void
  activePreset: TaskFilterPresetEnum | null
}

const TASK_FILTER_STATE_STORAGE_KEY = 'taskFilterState'

/**
 * Builds the shared set of MongoDB conditions from TaskFilterState.
 * When `options.includeStatus` is true, the status condition is included.
 */
const buildFilterConditions = (
  state: TaskFilterState,
  resolvedDateRange?: DateRange | null,
  options?: { includeStatus?: boolean }
): Record<string, any> => {
  const conditions: Record<string, any>[] = []

  // Status — only add if requested and not all statuses are selected
  if (options?.includeStatus && !isEmpty(state.statuses) && state.statuses.length < ALL_TASK_STATUSES.length) {
    conditions.push({ status: { $in: state.statuses } })
  }

  // Project
  if (!isEmpty(state.projectIds)) {
    conditions.push({ 'project._id': { $in: state.projectIds } })
  }

  // Work Order
  if (!isEmpty(state.workOrderIds)) {
    conditions.push({ 'workOrder._id': { $in: state.workOrderIds } })
  }

  // Assignee — members is ObjectId[], NOT embedded docs
  const assigneeCondition = buildAssigneeCondition(state.assigneeIds)
  if (assigneeCondition) {
    conditions.push(assigneeCondition)
  }

  // Date range — resolved by the hook using user's timezone
  if (resolvedDateRange) {
    conditions.push({ endDate: resolvedDateRange })
  }

  if (isEmpty(conditions)) return {}
  if (conditions.length === 1) return conditions[0]
  return { $and: conditions }
}

/**
 * Builds a MongoDB filter object from the given TaskFilterState.
 * This is a pure function exported separately for testability.
 */
export const buildTaskFilter = (state: TaskFilterState, resolvedDateRange?: DateRange | null): Record<string, any> =>
  buildFilterConditions(state, resolvedDateRange, { includeStatus: true })

/**
 * Resolves the dateRange from TaskFilterState into a MongoDB DateRange ($gte/$lte).
 * Handles both presets (DateNameEnum → DateNameValue → getDynamicDateFilter) and custom ranges.
 * Requires user's timezone for correct date boundary calculations.
 */
export const resolveDateFilter = (dateRange: TaskFilterDateRange, timeZone?: string): DateRange | null => {
  if (dateRange.presetName) {
    const template = DateNameValue[dateRange.presetName]
    if (!template) return null
    const resolvedGte = template.$gte
      ? getDynamicDateFilter({ filterString: template.$gte as string, timeZone })
      : undefined
    const resolvedLte = template.$lte
      ? getDynamicDateFilter({ filterString: template.$lte as string, timeZone })
      : undefined
    return { $gte: resolvedGte, $lte: resolvedLte }
  }

  if (dateRange.customStart ?? dateRange.customEnd) {
    const result: DateRange = {}
    if (dateRange.customStart) {
      result.$gte = toDate(dateRange.customStart, timeZone, { keepLocalTime: true }).startOf('day').toISOString()
    }
    if (dateRange.customEnd) {
      result.$lte = toDate(dateRange.customEnd, timeZone, { keepLocalTime: true }).endOf('day').toISOString()
    }
    return result
  }

  return null
}

/**
 * Builds a MongoDB filter object from the given TaskFilterState, excluding the status condition.
 * Used by status summary tabs to get per-status counts with all other filters applied.
 */
export const buildNonStatusFilter = (
  state: TaskFilterState,
  resolvedDateRange?: DateRange | null
): Record<string, any> => buildFilterConditions(state, resolvedDateRange)

// Date helpers removed — date resolution now uses DateNameValue + resolveDateFilter with timezone

const detectActivePreset = (state: TaskFilterState, userId?: string): TaskFilterPresetEnum | null => {
  // "My Tasks" — only assigneeIds matches current user
  if (
    userId &&
    state.assigneeIds.length === 1 &&
    state.assigneeIds[0] === userId &&
    state.statuses.length === DEFAULT_TASK_FILTER_STATE.statuses.length &&
    state.statuses.every((s) => DEFAULT_TASK_FILTER_STATE.statuses.includes(s)) &&
    isEmpty(state.projectIds) &&
    isEmpty(state.workOrderIds) &&
    !state.dateRange.presetName &&
    !state.dateRange.customStart &&
    !state.dateRange.customEnd
  ) {
    return TaskFilterPresetEnum.MyTasks
  }

  if (state.dateRange.presetName === DateNameEnum.ThisWeek) {
    return TaskFilterPresetEnum.DueThisWeek
  }

  if (state.dateRange.customEnd && !state.dateRange.customStart && !state.dateRange.presetName) {
    return TaskFilterPresetEnum.Overdue
  }

  return null
}

export const useTaskFilterState = (): UseTaskFilterStateResult => {
  const { user } = useUser()
  const timeZone = useUserTimeZone()
  const [stored, setStored] = useLocalStorage<TaskFilterState>(TASK_FILTER_STATE_STORAGE_KEY)

  const defaultState = useMemo(() => getDefaultFilterState(user?._id), [user?._id])

  const filterState = useMemo<TaskFilterState>(() => stored ?? defaultState, [stored, defaultState])

  // Resolve date filter using user's timezone
  const resolvedDateRange = useMemo(
    () => resolveDateFilter(filterState.dateRange, timeZone),
    [filterState.dateRange, timeZone]
  )

  const mongoFilter = useMemo(() => buildTaskFilter(filterState, resolvedDateRange), [filterState, resolvedDateRange])

  const nonStatusFilter = useMemo(
    () => buildNonStatusFilter(filterState, resolvedDateRange),
    [filterState, resolvedDateRange]
  )

  const setFilter = useCallback(
    (partial: Partial<TaskFilterState>) => {
      // When dateRange is provided, replace entirely (don't merge) to avoid
      // stale presetName persisting alongside custom dates or vice versa
      const newDateRange = partial.dateRange ?? filterState.dateRange
      setStored({ ...filterState, ...partial, dateRange: newDateRange })
    },
    [filterState, setStored]
  )

  const clearFilter = useCallback(
    (key: keyof TaskFilterState) => {
      setStored({ ...filterState, [key]: defaultState[key] })
    },
    [filterState, defaultState, setStored]
  )

  const clearAll = useCallback(() => {
    setStored(defaultState)
  }, [defaultState, setStored])

  const applyPreset = useCallback(
    (preset: TaskFilterPresetEnum) => {
      switch (preset) {
        case TaskFilterPresetEnum.MyTasks: {
          setStored(defaultState)
          break
        }
        case TaskFilterPresetEnum.DueThisWeek: {
          setStored({
            ...defaultState,
            dateRange: { presetName: DateNameEnum.ThisWeek },
          })
          break
        }
        case TaskFilterPresetEnum.Overdue: {
          const todayEnd = toDate(undefined, timeZone, { keepLocalTime: true }).endOf('day').format('YYYY-MM-DD')
          setStored({
            ...defaultState,
            statuses: [TaskStatusEnum.Draft, TaskStatusEnum.InProgress],
            dateRange: { customEnd: todayEnd },
          })
          break
        }
      }
    },
    [defaultState, timeZone, setStored]
  )

  const activePreset = useMemo(() => detectActivePreset(filterState, user?._id), [filterState, user?._id])

  return {
    filterState,
    mongoFilter,
    nonStatusFilter,
    setFilter,
    clearFilter,
    clearAll,
    applyPreset,
    activePreset,
  }
}
